/**
 * <recently-viewed-products>
 *
 * Tracks recently viewed product IDs in localStorage and, when placed
 * on a page, fetches those products via Shopify's /search endpoint and
 * renders them using the section's existing Liquid markup (product-card,
 * carousel, etc.) by requesting the section HTML directly.
 *
 * Expected usage (matches the section snippet you shared):
 *   <recently-viewed-products products-count="9" exclude-id="123456789">
 *     ...server-rendered fallback markup (search results), or empty...
 *   </recently-viewed-products>
 */

(function () {
  const STORAGE_KEY = 'recently-viewed-products';
  const MAX_STORED = 24; // keep a bit more than any section will ever request

  class RecentlyViewedProducts extends HTMLElement {
    connectedCallback() {
      // 1. Always record the current product (if we're on a product page)
      this.recordCurrentProduct();

      // 2. Populate this instance with matching products
      this.render();
    }

    /**
     * If we're on a product page, push/move this product ID to the front
     * of the stored list.
     */
    recordCurrentProduct() {
      const productId = this.getCurrentProductId();
      if (!productId) return;

      let ids = this.getStoredIds();

      // Remove if already present, then add to the front (most recent first)
      ids = ids.filter((id) => id !== productId);
      ids.unshift(productId);

      // Cap the list length
      ids = ids.slice(0, MAX_STORED);

      this.setStoredIds(ids);
    }

    /**
     * Reads the current product ID from Shopify's ShopifyAnalytics /
     * meta object, falling back to a data attribute you can render
     * server-side on product pages if needed.
     */
    getCurrentProductId() {
      try {
        if (
          window.ShopifyAnalytics &&
          window.ShopifyAnalytics.meta &&
          window.ShopifyAnalytics.meta.page &&
          window.ShopifyAnalytics.meta.page.resourceType === 'product'
        ) {
          return String(window.ShopifyAnalytics.meta.product.id);
        }
      } catch (e) {
        /* no-op, fall through */
      }

      // Fallback: a global set by the theme on product templates, e.g.
      // <script>window.__productId = {{ product.id | json }};</script>
      if (window.__productId) return String(window.__productId);

      return null;
    }

    getStoredIds() {
      try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.map(String) : [];
      } catch (e) {
        return [];
      }
    }

    setStoredIds(ids) {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
      } catch (e) {
        /* localStorage unavailable (private mode, quota, etc.) — fail silently */
      }
    }

    /**
     * Builds the list of IDs to display: stored IDs minus the excluded
     * (current) product, capped to products-count.
     */
    getDisplayIds() {
      const excludeId = this.getAttribute('exclude-id');
      const count = parseInt(this.getAttribute('products-count'), 10) || 9;

      let ids = this.getStoredIds();
      if (excludeId) {
        ids = ids.filter((id) => id !== String(excludeId));
      }

      return ids.slice(0, count);
    }

    async render() {
      const ids = this.getDisplayIds();

      if (ids.length === 0) {
        // Nothing to show — leave empty so the section's :has(...) CSS
        // rule hides it.
        this.innerHTML = '';
        return;
      }

      // Build a search query the section's Liquid already knows how to
      // parse: "id:123 OR id:456 OR id:789"
      const query = ids.map((id) => `id:${id}`).join(' OR ');

      // Find the section id from this element's closest section wrapper,
      // e.g. id="shopify-section-abc123"
      const sectionWrapper = this.closest('[id^="shopify-section-"]');
      const sectionId = sectionWrapper
        ? sectionWrapper.id.replace('shopify-section-', '')
        : null;

      if (!sectionId) {
        console.warn('recently-viewed-products: could not resolve section id');
        return;
      }

      const url =
        `/search?q=${encodeURIComponent(query)}` +
        `&type=product&options%5Bunavailable_products%5D=hide` +
        `&section_id=${encodeURIComponent(sectionId)}`;

      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Search request failed: ${response.status}`);

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        // Pull the fresh <recently-viewed-products> content out of the
        // fetched section markup and swap it into this element.
        const fresh = doc.querySelector('recently-viewed-products');

        if (fresh && fresh.innerHTML.trim()) {
          this.innerHTML = fresh.innerHTML;
        } else {
          this.innerHTML = '';
        }
      } catch (error) {
        console.error('recently-viewed-products: failed to fetch products', error);
        this.innerHTML = '';
      }
    }
  }

  if (!customElements.get('recently-viewed-products')) {
    customElements.define('recently-viewed-products', RecentlyViewedProducts);
  }
})();